import React from 'react';
import PropTypes from 'prop-types';

const Serum = ({ searchTerm, isDarkMode }) => {
  const serumValues = {
    general: [
      {
        test: 'Sodium (Na+)',
        conventional: '136–146 mEq/L',
        si: '136–146 mmol/L'
      },
      {
        test: 'Potassium (K+)',
        conventional: '3.5–5.0 mEq/L',
        si: '3.5–5.0 mmol/L'
      },
      {
        test: 'Chloride (Cl–)',
        conventional: '95–105 mEq/L',
        si: '95–105 mmol/L'
      },
      {
        test: 'Bicarbonate (HCO3–)',
        conventional: '22–28 mEq/L',
        si: '22–28 mmol/L'
      },
      {
        test: 'Urea nitrogen',
        conventional: '7–18 mg/dL',
        si: '2.5–6.4 mmol/L'
      },
      {
        test: 'Creatinine',
        conventional: '0.6–1.2 mg/dL',
        si: '53–106 μmol/L'
      }
    ],
    hepatic: [
      {
        test: 'Alanine aminotransferase (ALT)',
        conventional: '10–40 U/L',
        si: '10–40 U/L'
      },
      {
        test: 'Aspartate aminotransferase (AST)',
        conventional: '12–38 U/L',
        si: '12–38 U/L'
      },
      {
        test: 'Alkaline phosphatase',
        conventional: '25–100 U/L',
        si: '25–100 U/L'
      }
    ],
    lipids: [
      {
        test: 'Cholesterol, Total',
        conventional: 'Normal: <200 mg/dL, High: >240 mg/dL',
        si: '<5.2 mmol/L, >6.2 mmol/L'
      },
      {
        test: 'HDL',
        conventional: '40–60 mg/dL',
        si: '1.0–1.6 mmol/L'
      },
      {
        test: 'LDL',
        conventional: '<160 mg/dL',
        si: '<4.2 mmol/L'
      }
    ],
    endocrine: [
      {
        test: 'TSH',
        conventional: '0.4–4.0 μU/mL',
        si: '0.4–4.0 mIU/L'
      },
      {
        test: 'Free T4',
        conventional: '0.9–1.7 ng/dL',
        si: '12.0–21.9 pmol/L'
      },
      {
        test: 'Cortisol (0800 h)',
        conventional: '5–23 μg/dL',
        si: '138–635 nmol/L'
      }
    ]
  };

  const filterData = (data, term) => {
    if (!term) return data;
    const lowerTerm = term.toLowerCase();
    
    return Object.keys(data).reduce((acc, key) => {
      acc[key] = data[key].filter(item => 
        item.test.toLowerCase().includes(lowerTerm) ||
        (typeof item.conventional === 'string' && 
         item.conventional.toLowerCase().includes(lowerTerm)) ||
        (typeof item.si === 'string' && 
         item.si.toLowerCase().includes(lowerTerm)) ||
        (item.conventional?.male?.toLowerCase().includes(lowerTerm)) ||
        (item.conventional?.female?.toLowerCase().includes(lowerTerm))
      );
      return acc;
    }, {});
  };

  const filteredValues = filterData(serumValues, searchTerm);

  return (
    <div className={isDarkMode ? 'ext_lbs_dark-mode' : ''}>
      {filteredValues.general.length > 0 && (
        <>
          <h2>General Chemistry</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.general.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{item.conventional}</td>
                    <td>{item.si}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.hepatic.length > 0 && (
        <>
          <h2>Hepatic Function</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.hepatic.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{item.conventional}</td>
                    <td>{item.si}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.lipids.length > 0 && (
        <>
          <h2>Lipid Profile</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.lipids.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{item.conventional}</td>
                    <td>{item.si}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {filteredValues.endocrine.length > 0 && (
        <>
          <h2>Endocrine Studies</h2>
          <div className="ext_lbs_labs-table_container">
            <table className="ext_lbs_labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Conventional Range</th>
                  <th>SI Range</th>
                </tr>
              </thead>
              <tbody>
                {filteredValues.endocrine.map((item, index) => (
                  <tr key={index}>
                    <td>{item.test}</td>
                    <td>{item.conventional}</td>
                    <td>{item.si}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

Serum.propTypes = {
  searchTerm: PropTypes.string,
  isDarkMode: PropTypes.bool
};

export default Serum; 